import { Stack } from "@mui/material";
import { useMemo } from "react";

import { splitGqlUnion } from "~/shared/helpers/splitGqlUnion";

import FormTemplateFormDisplay from "./FormTemplateFormDisplay";
import { createFieldList, filterNullValues } from "./formHelpers";
import { Shared_FormTemplateFragment } from "./fragments/fragments.generated";
import QuestionAmount from "./questions/QuestionAmount";
import QuestionDateTime from "./questions/QuestionDateTime";
import QuestionDropdown from "./questions/QuestionDropdown";
import QuestionSelect from "./questions/QuestionSelect";
import QuestionText from "./questions/QuestionText";
import { NarrowedFormField } from "./types";

type Props = {
    form: Shared_FormTemplateFragment;
    disabled?: boolean;
    readOnly?: boolean;
    values: Record<string, string | null>;
    onChange: (values: Record<string, string | null>) => void;
};

export default function FormTemplateForm({ form, values, onChange, disabled, readOnly }: Props) {
    const visibleFields = useMemo(() => createFieldList(form, values), [form, values]);
    const filteredValues = useMemo(() => filterNullValues(values), [values]);

    if (readOnly) {
        return <FormTemplateFormDisplay form={form} values={values} />;
    }

    return (
        <Stack gap={2}>
            {visibleFields.map(q =>
                splitGqlUnion(q.type, {
                    GqlSettingsFormTemplateQuestionTypeAmount: t => (
                        <QuestionAmount
                            key={q.id}
                            field={NarrowedFormField.create(q, t)}
                            value={filteredValues[q.key] ?? ""}
                            onChange={value => onChange({ ...values, [q.key]: value })}
                            disabled={disabled}
                        />
                    ),
                    GqlSettingsFormTemplateQuestionTypeRecording: () => null,
                    GqlSettingsFormTemplateQuestionTypeDropdown: t => (
                        <QuestionDropdown
                            key={q.id}
                            field={NarrowedFormField.create(q, t)}
                            value={filteredValues[q.key] ?? ""}
                            onChange={value => onChange({ ...values, [q.key]: value })}
                            disabled={disabled}
                        />
                    ),
                    GqlSettingsFormTemplateQuestionTypeSelect: t => (
                        <QuestionSelect
                            key={q.id}
                            field={NarrowedFormField.create(q, t)}
                            value={filteredValues[q.key] ?? ""}
                            onChange={value => onChange({ ...values, [q.key]: value })}
                            disabled={disabled}
                        />
                    ),
                    GqlSettingsFormTemplateQuestionTypeText: t => (
                        <QuestionText
                            key={q.id}
                            field={NarrowedFormField.create(q, t)}
                            value={filteredValues[q.key] ?? ""}
                            onChange={value => onChange({ ...values, [q.key]: value })}
                            disabled={disabled}
                        />
                    ),
                    GqlSettingsFormTemplateQuestionTypeDateTime: t => (
                        <QuestionDateTime
                            key={q.id}
                            field={NarrowedFormField.create(q, t)}
                            value={filteredValues[q.key] ?? ""}
                            onChange={value => onChange({ ...values, [q.key]: value })}
                            disabled={disabled}
                        />
                    ),
                }),
            )}
        </Stack>
    );
}
